import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Return } from '@/schemas/returns';

export type ReturnStatus = 'pending' | 'approved' | 'refunded' | 'rejected';

interface ReturnFilters {
  status?: ReturnStatus;
  dateRange?: {
    start: string;
    end: string;
  };
  saleId?: string;
  search?: string;
}

interface ReturnsState {
  byId: Record<string, Return>;
  allIds: string[];
  filters: ReturnFilters;
  selectedReturnId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ReturnsState = {
  byId: {},
  allIds: [],
  filters: {},
  selectedReturnId: null,
  isLoading: false,
  error: null,
};

const returnsSlice = createSlice({
  name: 'returns',
  initialState,
  reducers: {
    setReturns: (state, action: PayloadAction<Return[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((returnItem) => {
        state.byId[returnItem.id] = returnItem;
        state.allIds.push(returnItem.id);
      });
    },
    addReturn: (state, action: PayloadAction<Return>) => {
      const returnItem = action.payload;
      state.byId[returnItem.id] = returnItem;
      if (!state.allIds.includes(returnItem.id)) {
        state.allIds.push(returnItem.id);
      }
    },
    updateReturn: (state, action: PayloadAction<Return>) => {
      const returnItem = action.payload;
      state.byId[returnItem.id] = returnItem;
    },
    updateReturnStatus: (state, action: PayloadAction<{ id: string; status: string }>) => {
      const returnItem = state.byId[action.payload.id];
      if (returnItem) {
        returnItem.status = action.payload.status;
      }
    },
    removeReturn: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setFilters: (state, action: PayloadAction<Partial<ReturnFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setSelectedReturn: (state, action: PayloadAction<string | null>) => {
      state.selectedReturnId = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setReturns,
  addReturn,
  updateReturn,
  updateReturnStatus,
  removeReturn,
  setFilters,
  clearFilters,
  setSelectedReturn,
  setLoading,
  setError,
} = returnsSlice.actions;

export default returnsSlice.reducer;
